// What became of a firmware delivery, wherever an access point is drawn
// (ADR-0403, built by #1813, amending ADR-0371 `d.1`): the marker on a row of
// the site's device table and in the device page's head (`1a`, `3a`), and the
// device page's body, which keeps the newest delivery and Orbit's steps (`1a`,
// `4.1a`); since #1841 that body is the one Firmware card's result state, under
// the card's title and its × (`FirmwareCard.tsx`). Since #1822 (ADR-0410) it is a story
// read top to bottom, labels and values only: *From → To* and the state, when
// it started, the step bar with each step's time from the click, then the readings.
// This service's liveness is not drawn here (ADR-0410 decision 1). Since #1832 (ADR-0413) a
// delivery refused before anything was sent reads *Not sent* with no bar, its next step is a
// line of its own, and nothing names a way back or a rollback (decisions 7 and 11). Since #1845 a
// `force_available` step is the force itself: one button, under its one failure line. Since #1880
// (ADR-0433) a second sentence under that line names the step the rollout takes first, held closed
// until the rollout takes it (`ORBIT_ENSURES_CONTROLLER_PAIR`).
//
// **Everything here draws the server's answer and decides nothing.** The state
// is the server's, mapped from Orbit's and never inferred from liveness, the
// firmware string or time; the steps are Orbit's, named by the server.
// Nothing reads *upgraded* or *delivered* before the server says `delivered`.
import { useEffect, useState, type ReactNode } from "react";
import { UNATTENDED } from "../../api/hooks";
import type { components } from "../../api/schema";
import { Pill } from "../../ui";
import { type Device, type FirmwareDelivery } from "./commands";
import { listed, unrecognized } from "../../api/openEnum";

type Step = components["schemas"]["FirmwareDeliveryStep"];

/** A digest shortened for reading. */
const short = (digest: string) => digest.replace(/^sha256:/, "").slice(0, 12);

/** Re-reads through `reload` whenever the change stream says a firmware
 * delivery moved (ADR-0240, ADR-0371 `d.1a`). The announcement names the cell
 * and not the device, so a page re-reads on any delivery in its cell. No
 * `EventSource` is a stream that is down, and the page then shows what it
 * last read, as the request panel always has. */
export function useDeliveryChanges(reload: (how?: typeof UNATTENDED) => void, on = true) {
  useEffect(() => {
    if (!on || typeof EventSource === "undefined") return;
    const stream = new EventSource("/v1/changes");
    stream.addEventListener("change", (ev) => {
      try { if (JSON.parse((ev as MessageEvent).data).kind !== "firmware_delivery") return; } catch { /* re-read anyway */ }
      reload(UNATTENDED);
    });
    return () => stream.close();
  }, [reload, on]);
}

/** The marker's kind for a delivery's state, or none.
 *
 * Restates ADR-0403 decision 3a and ADR-0371 decision d.3a — one marker for
 * `handing_over`, `accepted` and `in_progress`, exactly the states a second
 * click is refused in, so the marker shows precisely when another upgrade
 * would be refused. `unknown` has its own marker and is neither under way nor
 * done. A final state carries no marker: the outcome is on the device page. */
export function markerOf(state: FirmwareDelivery["state"]): "under_way" | "not_known" | undefined {
  switch (state) {
    case "handing_over": case "accepted": case "in_progress": return "under_way";
    case "unknown": return "not_known";
    default: return undefined;
  }
}

/** The marker itself. **Its words describe the act and never the device**
 * (ADR-0403 `3a`): Zenith knows what it asked the rollout and what the rollout
 * answered, not what the access point is doing. */
// Restates ADR-0403 decision 3a — the marker says an upgrade is under way or that its outcome is
// not known, names no duration and no progress, and never says the device is upgraded or flashing.
export function DeliveryMarker({ delivery }: { delivery?: FirmwareDelivery }) {
  const kind = delivery && markerOf(delivery.state);
  if (kind === "under_way") return <Pill tone="info">Upgrade under way</Pill>;
  if (kind === "not_known") return <Pill tone="none">Upgrade outcome not known</Pill>;
  return null;
}

/** The stages the bar draws (ADR-0410 decision 7), in order: the segments it
 * draws when Orbit has not recorded them yet. Orbit's `accepted` and
 * `command_committed` are one stage, *Queued*. */
const STAGES = ["queued", "upgrading", "back_online", "confirmed"] as const;
type Stage = (typeof STAGES)[number] | "refused" | "stopped" | "unrecognised";

/** Which stage one of Orbit's steps belongs to. */
function stageOf(step: Step["step"]): Stage {
  switch (step) {
    case "accepted": case "command_committed": return "queued";
    case "controller_answered": return "upgrading";
    case "device_returned": return "back_online";
    case "confirmed": return "confirmed";
    case "refused": return "refused";
    case "stopped": return "stopped";
    default: return "unrecognised";
  }
}

/** A stage as its segment of the bar names it (ADR-0410 decision 7). */
// Restates ADR-0403 decision 4.1a as ADR-0410 decision 7 amends it — Orbit's steps in Orbit's order,
// accepted and committed drawn as one *Queued*, and no label reads as delivered before the rollout
// confirms. *Upgrading* claims a little more than "the controller answered" says, and was accepted
// as such.
export function stageLabel(stage: Stage): string {
  switch (stage) {
    case "queued": return "Queued";
    case "upgrading": return "Upgrading";
    case "back_online": return "Back online";
    case "confirmed": return "Confirmed";
    case "refused": return "Refused";
    case "stopped": return "Stopped";
    default: return "Unnamed step";
  }
}

/** A reason as a short label (#1822, ADR-0410 decision 3): Zenith's own reason,
 * mapped from Orbit's code (ADR-0371 `d.2a`), in a few words. */
// Restates ADR-0371 decision d.2a, as ADR-0413 decisions 7.1 and 11 word it — the reason is Zenith's,
// mapped from Orbit's code. A refusal before anything was sent no longer adds "nothing sent": the
// card's word, *Not sent*, says it. No label names a way back; `no_rollback_target` says what is
// missing. An unmapped code reads as a refusal or a failure.
const REASON_LABEL: Partial<Record<components["schemas"]["FirmwareDeliveryReason"], string>> = {
  entry_unknown: "Firmware not in the catalog",
  entry_not_fleet_ready: "Not cleared for the fleet",
  entry_not_vendor_access_point: "Not an access point image",
  outside_staging: "Site's environment not served",
  site_unplaced: "Site not settled on its controller",
  device_not_at_site: "Device not found at the site",
  device_unread: "Device could not be read",
  compatible_mismatch: "Hardware does not match",
  source_build_unread: "Running build not read for this path",
  no_rollback_target: "Upgrade from this device's firmware not cleared yet",
  delivery_in_progress: "Another delivery under way",
  digest_mismatch: "Image digest mismatch",
  fetch_redirected: "Download redirected",
  placement_moved: "Site moved controllers",
  orbit_unavailable: "Rollout could not prepare",
  sender_refused: "Stopped before the controller",
  delivery_blocked: "Paused for this model and build",
  override_not_applicable: "Refusal no longer applies as cited",
  short_body_unread: "Incomplete-download reading not recorded",
  keep_redirector_unrecorded: "Controller-keeping reading not recorded",
  canary_unconfirmed: "Waiting for a first upgrade to confirm",
  firmware_delivery_refused: "Refused",
  not_handed_over: "Not handed to the rollout",
  controller_refused: "Controller did not accept the command",
  did_not_return: "Device did not come back in time",
  firmware_line_mismatch: "Device came back on another firmware",
  upgrade_failed: "Upgrade failed",
  root_digest_mismatch: "Firmware on the device does not match",
  firmware_delivery_failed: "Stopped after the device was told",
  handoff_unanswered: "Rollout did not answer",
  handoff_conflict: "Rollout's record differs",
  operation_not_found: "Rollout no longer finds it",
  orbit_unreadable: "Rollout could not be read",
  state_unrecognised: "Rollout state not recognised",
  outcome_unknown: "What followed is not known",
};

export function reasonLabel(reason: string | undefined): string | undefined {
  if (!reason) return undefined;
  return listed(REASON_LABEL, reason) ? REASON_LABEL[reason] ?? "Reason not recognised" : unrecognized(reason);
}

/** The next step as a line of its own (ADR-0411 `7a`, as ADR-0413 `7.3a` draws it). **The
 * server chooses the step** from the ruled paths, the device's line and the caller; this file only
 * names it, and `none` adds nothing. */
// Restates ADR-0411 decisions 5b, 7a and 3.A, as ADR-0413 decisions 5 and 11 word them — only the
// provider may upgrade anyway, a customer is told to ask the provider, a provider is told to wait for
// a reading while that upgrade is not open, and a device that reported no firmware is told to report
// it. It names no product and no way back, and it never offers the upgrade where the server did not.
export const NEXT_STEP_LABEL: Record<components["schemas"]["FirmwareDeliveryNextStep"], string | undefined> = {
  force_available: "Upgrade anyway",
  ask_provider: "Ask your provider",
  wait_for_reading: "Wait for a reading",
  report_firmware: "Device must report its firmware",
  none: undefined,
};

/** One segment of the bar: a step Orbit recorded, or one of the steps that
 * make a delivery that it has not recorded yet. */
interface Segment {
  key: string; name: Stage; kind: "recorded" | "refused" | "stopped" | "unnamed" | "pending"; step?: Step;
  /** When the step was recorded, in seconds from the click: Orbit's `recorded_at` less the
   * delivery's `created_at`, which is this service's clock, so the two clocks' skew is in it. */
  offset?: number;
}

/** Whole seconds from one time to the next, signed and never clamped (#1822,
 * ADR-0410 decision 2). `undefined` where either cannot be read. */
export function secondsBetween(from: string, to: string): number | undefined {
  const a = Date.parse(from), b = Date.parse(to);
  return Number.isNaN(a) || Number.isNaN(b) ? undefined : Math.round((b - a) / 1000);
}

/** A step's time from the click, as its segment says it: `+12 s` under a
 * minute, else `+5 min 49 s`. The offset sets Orbit's `recorded_at` against
 * this service's `created_at`, two clocks, so it is shown as it is and never
 * clamped: `−2 s` is the clocks disagreeing. */
// Restates ADR-0410 decision 2 — a step's measured time from the click, and nothing else: no ETA, no
// expected duration, no judgement and no counter that runs while the page is open.
export function offsetWords(sec: number | undefined): string {
  if (sec === undefined) return "time not read";
  const sign = sec < 0 ? "\u2212" : "+", n = Math.abs(sec);
  return n < 60 ? `${sign}${n} s` : `${sign}${Math.floor(n / 60)} min ${String(n % 60).padStart(2, "0")} s`;
}

/** The bar's segments, drawn as Orbit recorded them and never straightened
 * out (#1822): every recorded step in Orbit's order (`seq`), each with its time
 * from the click, except that `accepted` and `command_committed` are one
 * *Queued* segment, timed at `command_committed` where it is recorded and at
 * `accepted` where it is not (ADR-0410 decision 7); a refusal or a stop ending
 * the bar where it was recorded; a step this release does not name drawn as
 * one; and after the last stage recorded, the ones not recorded yet. A
 * delivery the server reads `refused` or `failed` is over, so nothing is drawn
 * as still to come. */
export function segmentsOf(d: FirmwareDelivery, steps: Step[]): Segment[] {
  const sorted = [...steps].sort((x, y) => x.seq - y.seq);
  const segs: Segment[] = [];
  for (const s of sorted) {
    const name = stageOf(s.step);
    const offset = secondsBetween(d.created_at, s.recorded_at);
    const queued = name === "queued" ? segs.find((g) => g.name === "queued") : undefined;
    if (queued) {
      // The later of the two: committed's time wins, and accepted's is kept only where committed is not recorded.
      if (s.step === "command_committed" || queued.step?.step !== "command_committed") { queued.step = s; queued.offset = offset; }
      continue;
    }
    segs.push({
      key: `seq-${s.seq}`, name, step: s, offset,
      kind: name === "refused" ? "refused" : name === "stopped" ? "stopped" : name === "unrecognised" ? "unnamed" : "recorded",
    });
  }
  if (segs.some((g) => g.kind === "refused" || g.kind === "stopped")) return segs;
  if (d.state === "refused" || d.state === "failed") return segs;
  const last = Math.max(-1, ...segs.map((g) => (STAGES as readonly string[]).indexOf(g.name)));
  return [...segs, ...STAGES.slice(last + 1).map((name): Segment => ({ key: `pending-${name}`, name, kind: "pending" }))];
}

/** What a screen reader hears after a segment's label. */
const SEGMENT_SAID: Record<Segment["kind"], string> = {
  recorded: "recorded", unnamed: "recorded", refused: "recorded, the last step",
  stopped: "recorded, the last step", pending: "not recorded yet",
};

/** Orbit's steps as a bar of segments, oldest first (ADR-0403 `4.1a`, #1822):
 * each a label and its time from the click, a refusal or a stop with its reason's
 * label. **It is a list and not a progress figure**: no `<progress>`, no
 * `role="progressbar"`, no count, no percentage and no fill that moves with
 * time. It reads as done only where the server's `state` is `delivered`, never
 * on a `confirmed` step. Absent steps and an empty list each have a status of
 * their own. */
export function DeliverySteps({ d }: { d: FirmwareDelivery }) {
  if (!d.steps) return <Pill tone="none">Steps not read yet</Pill>;
  if (d.steps.length === 0) return <Pill tone="none">No step recorded</Pill>;
  return (
    <ol className="step-bar" aria-label="The firmware rollout's steps" data-state={d.state} data-outcome={outcomeOf(d).key}>
      {segmentsOf(d, d.steps).map((g) => (
        <li key={g.key} data-step={g.kind}>
          <span className="step-label">{stageLabel(g.name)}<span className="sr-only">: {SEGMENT_SAID[g.kind]}</span></span>
          {g.step && <span className="step-time">{offsetWords(g.offset)}</span>}
          {(g.kind === "refused" || g.kind === "stopped") && (reasonLabel(g.step?.reason ?? d.reason)) && (
            <span className="step-reason">{reasonLabel(g.step?.reason ?? d.reason)}</span>
          )}
        </li>
      ))}
    </ol>
  );
}


/** The build a delivery takes the device from (#1822, ADR-0410 decision 4):
 * the build the click's own read recorded. Where the click recorded none (a
 * row from before the request path kept it), the build the page already shows
 * for the device, while the rollout has not reported the delivery done; once it
 * has, that reading is the new build, so nothing is said. Never *not recorded*. */
export function fromBuild(d: FirmwareDelivery, current?: string): string | undefined {
  if (d.from_firmware) return d.from_firmware;
  if (d.state === "delivered") return undefined;
  return current || undefined;
}

/** The page's word for a delivery (ADR-0410 decision 5, as ADR-0413 `7.1a` amends it), one of five
 * and only five: *In progress*, *Delivered*, *Failed*, *Not sent* or *Unknown*. Decided from the
 * server's `state` and from `device_reading.comparison`, which the server computes; the API's state
 * is unchanged. **A delivery the rollout reports delivered reads Failed here where the device reports
 * another build, or where its build cannot be compared with the one predicted**, and **a refusal reads
 * Not sent**, because nothing reached the device. */
// Restates ADR-0371 decision b as ADR-0410 decision 5 and ADR-0413 decision 7.1a amend its display —
// Delivered only on the rollout's own report and a device reading that matches it or is absent; Not
// sent, in a neutral tone, where the rollout refused before anything reached the device; Failed where
// it failed, or reported delivered while the device's reading differs or cannot be compared; Unknown
// is neither under way nor done.
export function outcomeOf(d: FirmwareDelivery): { label: string; tone: "info" | "good" | "critical" | "none" | "warn"; key: "in_progress" | "delivered" | "failed" | "not_sent" | "unknown" | "unrecognised" } {
  const failed = { label: "Failed", tone: "critical", key: "failed" } as const;
  switch (d.state) {
    case "refused": return { label: "Not sent", tone: "none", key: "not_sent" };
    case "failed": return failed;
    case "unknown": return { label: "Unknown", tone: "none", key: "unknown" };
    case "delivered": {
      const c = d.device_reading?.comparison;
      return c === "differs" || c === "not_comparable" ? failed : { label: "Delivered", tone: "good", key: "delivered" };
    }
    case "handing_over": case "accepted": case "in_progress": return { label: "In progress", tone: "info", key: "in_progress" };
    // Restates ADR-0511 decision 5a — a state this build does not know is shown as received, marked,
    // and read as neither under way, done nor `unknown`.
    default: return { label: unrecognized(d.state), tone: "warn", key: "unrecognised" };
  }
}

/** Whether nothing past a refusal was recorded (ADR-0413 `7.2a`): a refused delivery whose steps,
 * where read, are the refusal alone or none. Such a delivery draws no bar. */
export function refusedBeforeAnyStep(d: FirmwareDelivery): boolean {
  return d.state === "refused" && (d.steps ?? []).every((s) => s.step === "refused");
}

/** The headline: where the delivery takes the device from and to, and its state. */
// Restates ADR-0371 decision a.2 and ADR-0410 decision 4 — "To" is the build the entry predicts and
// never a reading; without one the image is named by its digest, never as a version. "From" is the
// build the device reported at the click.
export function DeliveryHeadline({ d, current }: { d: FirmwareDelivery; current?: string }) {
  const state = outcomeOf(d);
  const from = fromBuild(d, current);
  return (
    <p className="delivery-headline">
      {from && <>
        <span className="delivery-build"><span className="delivery-key">From</span> {from}</span>
        <span className="delivery-arrow" aria-hidden="true">{"\u2192"}</span>
      </>}
      <span className="delivery-build"><span className="delivery-key">To</span> {d.predicted_firmware ?? <>image <code>{short(d.artifact)}</code></>}</span>
      <Pill tone={state.tone}>{state.label}</Pill>
    </p>
  );
}

/** Whether the firmware rollout this release hands deliveries to first makes sure a forced
 * delivery's unit holds its own operational certificate in its certificate store, copying it there
 * where it is absent, before it sends the upgrade (orbit ADR-0123 `1d`, `3a` and `4a`, Accepted
 * 2026-10-19; built by arpiq/orbit#932). It gates the one sentence ADR-0433 `1a` adds under the
 * force's failure line, and nothing else.
 *
 * **It is `false` until arpiq/orbit#932 runs on the Orbit staging hands deliveries to** (ADR-0433
 * decision 2). Today Orbit sends a forced delivery one command, the upgrade, and the sentence would
 * tell a person approving *Upgrade anyway* about a write that does not happen. Where the step runs
 * and this is still `false`, the screen is what it was before ADR-0433: the force's one line, and a
 * second write the person approves unnamed, which is what #1880 exists to end. So the sentence is
 * true in each direction only while this matches the rollout, and opening it is a commit made once
 * #932's build is read running on staging, which changes the test that pins it
 * (`Delivery.test.tsx`, *The force's step sentence › is held until the rollout takes the step*).
 *
 * **A fact about the peer this release was written against, never a setting**, for the reasons
 * `firmwaredelivery.OrbitServesOverrides` gives (`docs/ZENITH.md` §25: environment values are
 * references, never feature switches): Orbit's contract states nothing about the step today, and
 * inferring it from an answer's shape would be a guess about Orbit's build. It is not a §29 value.
 *
 * **Void with ADR-0433 if reading R1 (arpiq/orbit#931) sends orbit ADR-0123 to `1c`**: Orbit then
 * refuses instead of copying, and this constant and the sentence go with it. */
export const ORBIT_ENSURES_CONTROLLER_PAIR = false;

/** The force as the result view offers it (#1845, Gabriel Provencher, 2026-10-17: *"Why do I have to
 * click twice on upgrade anyway, on two different screens ?"*): one button on the refused delivery's
 * card that sends the forced request itself, with the one failure line above it. Absent where the
 * person may not take it; the step is then text. */
export interface ForceControl {
  /** The device's name, for the failure line. */
  name: string;
  onClick(): void;
  disabled?: boolean;
  title?: string;
}

/** The delivery as a story read top to bottom, minimalist (ADR-0410 decision
 * 3): the headline, when it started, and the step bar where `steps` asks for
 * it and something past a refusal was recorded (ADR-0413 `7.2a`). Below it, a
 * value line only where there is something the headline does not say: the
 * build the device runs where that made the delivery Failed (or that it was
 * not read), the build the rollout read where it is not the one predicted, a
 * reason no segment of the bar carries, and the next step on a line of its own
 * (ADR-0413 `7.3a`); for `force_available`, the force itself, one click under its failure line (#1845). */
// Restates ADR-0371 decisions a.2 and d.2a — the device's own build and the rollout's own reading
// are each shown as what they are, and the reason is Zenith's, mapped from Orbit's code.
//
// Restates ADR-0411 decisions 8a and 10a with ADR-0410 decision 3, as ADR-0413 decision 11 words
// them — a forced delivery says, as a value and not a sentence, that it was forced, and nothing names
// a way back; the line a refused device reported is the provider's alone and shown whole to be
// copied, and it is not a recorded reading of the firmware.
export function DeliveryStory({ d, steps = false, current, force, pairStep = ORBIT_ENSURES_CONTROLLER_PAIR }: {
  d: FirmwareDelivery; steps?: boolean; current?: string; force?: ForceControl;
  /** Whether the force's step sentence is drawn: `ORBIT_ENSURES_CONTROLLER_PAIR`, passed in so a
   * test can draw both. */
  pairStep?: boolean;
}) {
  const drawBar = steps && !refusedBeforeAnyStep(d);
  const barShowsReason = drawBar && d.steps?.some((s) => s.step === "refused" || s.step === "stopped");
  const reason = !barShowsReason ? reasonLabel(d.reason) : undefined;
  const next = listed(NEXT_STEP_LABEL, d.next_step) ? NEXT_STEP_LABEL[d.next_step] : unrecognized(d.next_step);
  const reading = d.state === "delivered" && d.device_reading && d.device_reading.comparison !== "matches" ? d.device_reading : undefined;
  const rollout = d.orbit_reading?.firmware && d.predicted_firmware && d.orbit_reading.firmware !== d.predicted_firmware ? d.orbit_reading.firmware : undefined;
  const forced = !!d.forced;
  const line = d.source_line;
  return (
    <div className="delivery-story">
      <DeliveryHeadline d={d} current={current} />
      <p className="delivery-started">Started at <time dateTime={d.created_at}>{new Date(d.created_at).toLocaleString()}</time></p>
      {drawBar && <DeliverySteps d={d} />}
      {(reason || next || reading || rollout || forced || line) && (
        <dl className="delivery-facts">
          {forced && <div><dt>Upgrade</dt><dd>Forced</dd></div>}
          {reading && (reading.firmware
            ? <div><dt>Device runs</dt><dd><code>{reading.firmware}</code></dd></div>
            : <div><dt>Device build</dt><dd>not read</dd></div>)}
          {rollout && <div><dt>Rollout read</dt><dd><code>{rollout}</code></dd></div>}
          {reason && <div><dt>Reason</dt><dd>{reason}</dd></div>}
          {next && <div><dt>Next</dt><dd>{d.next_step === "force_available" && force
            ? <>
              {/* Restates ADR-0411 decisions 5b, 6a and 3.A, as ADR-0413 decision 5 (`5d`) and decision 11
                  draw them and as amended 2026-10-17 (#1845), with ADR-0415 decision 1 (`1d`, #1856) —
                  the force is one click on the refused delivery's own card, under the one line saying
                  what a failure costs: the worst outcome measured, that a unit may not come back
                  without someone at it, or at all. It names no product, no way back, no count and no
                  rate, and promises no outcome. ADR-0433 `1a` lets one sentence follow it, below, and
                  leaves its words as they are. */}
              <p className="warn">If this upgrade fails, {force.name} may not come back without someone at the device, or at all, and upgrades of this model may pause until someone clears it.</p>
              {pairStep && <>
                {/* Restates ADR-0433 decision 1 (`1a`), amending ADR-0413 decision 5d's one line, and orbit ADR-0123 decisions 1d, 3a and 4a —
                    the force's approval also approves the step the rollout takes first, on every forced
                    delivery and not only a TIP-born unit's: it makes sure the unit's own operational
                    certificate is in its certificate store, and copies it there only where it is absent.
                    It promises no outcome, and the failure line above it keeps its words (orbit
                    ADR-0123 `6a`, ADR-0415 `1d`). Drawn only while the rollout takes the step. */}
                <p className="force-step">This upgrade first makes sure {force.name}'s own operational certificate is in its certificate store, copying it there if it is absent.</p>
              </>}
              <button type="button" className="btn btn-danger" disabled={force.disabled} title={force.title} onClick={force.onClick}>{next}</button>
            </>
            : next}</dd></div>}
          {line && <div><dt>Running line</dt><dd><code>{line}</code></dd></div>}
        </dl>
      )}
    </div>
  );
}

/** How long a Delivered delivery stays on the device page after the server's last
 * recorded move of it (ADR-0410 decision 6): Gabriel Provencher's answer, *"6h"*,
 * of 2026-10-16. A product display choice, not a measured value. */
export const DELIVERED_SHOWN_FOR_MS = 6 * 60 * 60 * 1000;

/** Whether the device page draws a delivery (ADR-0410 decision 6): always,
 * except a *Delivered* one whose `updated_at` is more than six hours old by
 * the browser's clock. In progress, Failed and Unknown stay until a newer
 * delivery replaces them, or until the viewer closes the card on them (#1841,
 * `closedMarkOf`). Decided when the page renders: nothing ticks it. */
export function deliveryShown(d: FirmwareDelivery, now: number): boolean {
  if (outcomeOf(d).key !== "delivered") return true;
  const at = Date.parse(d.updated_at);
  return Number.isNaN(at) || now - at <= DELIVERED_SHOWN_FOR_MS;
}

/** The device page's Firmware card in its result state (ADR-0403 `1a`, `4.1a`, as #1841 merges the
 * Upgrade panel, this section and the notice into one card): the newest delivery as a story read top
 * to bottom, labels and values only (#1822), under the card's title and its ×. Nothing when the
 * device has had no delivery, or a Delivered one older than six hours. */
// Restates ADR-0403 decision 5 as ADR-0410 decision 1 amends it — this service's own liveness
// reading is not drawn beside the delivery: the page's head already shows it.
export function DeliverySection({ device, newest, now, force, onClose, pairStep }: {
  device: Device;
  newest?: FirmwareDelivery;
  /** The force, for a *Next* step of `force_available` (ADR-0413 `7.3a` as amended 2026-10-17,
   * #1845): one click here sends it. Absent where the person may not take it; the step is then text. */
  force?: ForceControl;
  /** Closes the card (#1841). Absent, no × is drawn. */
  onClose?: () => void;
  /** The force's step sentence, for a test to draw both ways; otherwise `ORBIT_ENSURES_CONTROLLER_PAIR`. */
  pairStep?: boolean;
  /** The browser's clock, for a test to set. Otherwise read once when the
   * section mounts, and nothing ticks it: the card mounts it again whenever a
   * re-read brings a delivery that moved (its `key`). */
  now?: number;
}) {
  const [mounted] = useState(() => Date.now());
  if (!newest || !deliveryShown(newest, now ?? mounted)) return null;
  return (
    <FirmwareFrame onClose={onClose}>
      <DeliveryStory d={newest} steps current={device.firmware} force={force} pairStep={pairStep} />
    </FirmwareFrame>
  );
}

/** The one Firmware card's frame (#1841): its title and its ×, the same in every state. */
// Restates ADR-0413 decision 10 (`10a`) as amended 2026-10-17 (#1841) — the card closes at any
// time and in every state, by its × or, in the act, by Escape; closing sends nothing, cancels
// nothing and changes no delivery.
export function FirmwareFrame({ onClose, children }: { onClose?: () => void; children: ReactNode }) {
  return (
    <section className="panel" aria-label="Firmware">
      <div className="panel-head">
        <h2>Firmware</h2>
        {onClose && <button type="button" className="btn btn-quiet" aria-label="Close" onClick={onClose}>×</button>}
      </div>
      {children}
    </section>
  );
}

/** What a closed card remembers about the delivery it showed (#1841): its id and its
 * `updated_at`, the server's time of its last recorded move, so it comes back whenever the delivery
 * moves (a step recorded, a state reached) or a newer delivery replaces it. Gabriel Provencher,
 * 2026-10-17: *"per viewer in the browser"*, keyed as the coordinating session read it. */
export function closedMarkOf(d: FirmwareDelivery): string {
  return `${d.id}:${d.updated_at}`;
}

const closedKey = (serial: string) => `zenith.firmware-card.closed.${serial}`;

/** The mark of the delivery this viewer closed the card on, in this browser. A per-viewer
 * convenience (`web/README.md`): nothing about the delivery and nothing any other person sees
 * changes, and where the browser keeps nothing the card is simply shown. */
export function readClosed(serial: string): string | undefined {
  try { return window.localStorage.getItem(closedKey(serial)) ?? undefined; } catch { return undefined; }
}

export function writeClosed(serial: string, mark: string): void {
  try { window.localStorage.setItem(closedKey(serial), mark); } catch { /* shown again next time */ }
}
